import moment from 'moment';
import {
  ActionButton,
  Buttons,
  CancelButton,
  DayPickerInput,
  Drawer,
  HelpTooltip,
  Icon,
  SingleSelect,
  Table,
  TextInput,
  Tooltip,
} from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { dateFormats } from '~/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageLoader } from '~/routes/public/pages';
import Fuse from 'fuse.js';
import { colors } from '~/styles';
import _ from 'lodash';
import styled from 'styled-components';

const CustomContainer = styled.div`
  width: 100%;
  input {
    font-size: 0.875rem;
  }
`;

const DatePickerContainer = styled.div`
  width: 100%;
`;

export default function ConfigurationDrawer({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { xeroConnectionId } = useParams();

  const [configuration, setConfiguration] = useState({ data: null, isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getConfiguration();

      if (!isMounted.current) return;
      setConfiguration({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  const [items, setItems] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getItems();

      if (!isMounted.current) return;
      setItems({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  const [invoiceItems, setInvoiceItems] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).invoiceItems().get();

      if (!isMounted.current) return;
      setInvoiceItems({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted]);

  const [invoiceStatuses, setInvoiceStatuses] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getInvoiceStatuses();

      if (!isMounted.current) return;
      setInvoiceStatuses({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  const [paymentAccounts, setPaymentAccounts] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getPaymentAccounts();

      if (!isMounted.current) return;
      setPaymentAccounts({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  const [accounts, setAccounts] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getAccounts();

      if (!isMounted.current) return;
      setAccounts({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  const [expenseAccounts, setExpenseAccounts] = useState({ data: [], isReady: false });
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getExpenseAccounts();

      if (!isMounted.current) return;
      setExpenseAccounts({ data, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  return (
    <Drawer
      isOpen
      title="Configuration"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          if (configuration.data.expenseSyncEnabled && !configuration.data.expenseSyncStartDate) {
            toast.error('A sync start date is required when expense sync is enabled.');
            return;
          }

          form.submit();

          try {
            await api.www
              .workspaces(workspace.id)
              .xero(xeroConnectionId)
              .updateConfiguration({
                name: configuration.data.name,
                defaultXeroItemId: configuration.data.defaultXeroItemId,
                defaultXeroItemCode:
                  items.data.find((item) => item.id === configuration.data.defaultXeroItemId)?.code ?? null,
                defaultXeroAccountId: configuration.data.defaultXeroAccountId,
                defaultXeroAccountCode:
                  accounts.data.find((account) => account.id === configuration.data.defaultXeroAccountId)?.code ?? null,
                defaultInvoiceItemId: configuration.data.defaultInvoiceItemId,
                defaultInvoiceStatus: configuration.data.defaultInvoiceStatus,
                xeroPaymentAccountId: configuration.data.xeroPaymentAccountId,
                expenseSyncEnabled: configuration.data.expenseSyncEnabled,
                expenseSyncStartDate: configuration.data.expenseSyncStartDate,
                defaultExpenseAccountId: configuration.data.defaultExpenseAccountId,
                defaultExpenseAccountCode:
                  expenseAccounts.data.find((a) => a.id === configuration.data.defaultExpenseAccountId)?.code ?? null,
              });
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        const handleChange = (event) => {
          setDirty(true);

          setConfiguration((state) => ({
            ...state,
            data: { ...state.data, [event.target.name]: event.target.value || null },
          }));
        };

        const handleValueChange = (name, value) => {
          setDirty(true);

          setConfiguration((state) => ({
            ...state,
            data: { ...state.data, [name]: value },
          }));
        };

        if (
          !configuration.isReady ||
          !items.isReady ||
          !invoiceItems.isReady ||
          !invoiceStatuses.isReady ||
          !paymentAccounts.isReady ||
          !expenseAccounts.isReady
        )
          return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Setting</Table.Column>

                <Table.Column>
                  <strong>Value</strong>
                </Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                <Table.BoxRow>
                  <Table.Cell>
                    Integration Name
                    <HelpTooltip
                      message="The name of the Xero integration to be referenced in Ruddr."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <TextInput
                      container={CustomContainer}
                      name="name"
                      value={configuration.data.name}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default Ruddr Invoice Item
                    <HelpTooltip
                      message="Ruddr will use this default Invoice Item when updating a Ruddr invoice from Xero if the invoice uses a Product/Service that is not mapped to an Invoice Item in Ruddr."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={invoiceItems.data}
                      name="defaultInvoiceItemId"
                      typeLabel="Ruddr Invoice Item"
                      value={configuration.data.defaultInvoiceItemId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default Xero Product/Service
                    <HelpTooltip
                      message="Ruddr will use this default Product/Service when publishing an invoice to Xero if an Invoice Item in Ruddr is not mapped to a Product/Service or Account."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={items.data}
                      name="defaultXeroItemId"
                      typeLabel="Xero Product/Service"
                      value={configuration.data.defaultXeroItemId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default Xero Account
                    <HelpTooltip
                      message="Ruddr will use this default Account when publishing an invoice to Xero if an Invoice Item in Ruddr is not mapped to a Product/Service or Account."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={accounts.data}
                      name="defaultXeroAccountId"
                      typeLabel="Xero Account"
                      value={configuration.data.defaultXeroAccountId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default Xero Invoice Status
                    <HelpTooltip
                      message="This invoice status will be used in Xero when publishing an invoice from Ruddr."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={invoiceStatuses.data}
                      name="defaultInvoiceStatus"
                      typeLabel="Xero Invoice Status"
                      value={configuration.data.defaultInvoiceStatus}
                      onChange={handleChange}
                      showEmptyOption={false}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Payment Account
                    <HelpTooltip
                      message="Ruddr will use this Account when saving a payment to Xero."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={paymentAccounts.data}
                      name="xeroPaymentAccountId"
                      typeLabel="Xero Payment Account"
                      value={configuration.data.xeroPaymentAccountId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Reimbursable Expenses Sync
                    <HelpTooltip
                      message="When enabled, approved reimbursable expenses will be synced to Xero as bills."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <SingleSelect
                      value={configuration.data.expenseSyncEnabled ? 'yes' : 'no'}
                      onChange={(event) => {
                        const enabled = event.target.value === 'yes';
                        handleValueChange('expenseSyncEnabled', enabled);
                        if (enabled && !configuration.data.expenseSyncStartDate) {
                          handleValueChange('expenseSyncStartDate', moment().format(dateFormats.isoDate));
                        }
                      }}
                      showEmptyOption={false}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </SingleSelect>
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Reimbursable Expenses Sync Start Date
                    <HelpTooltip
                      message="Ruddr will only sync reimbursable expenses with a date on or after this date."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <DatePickerContainer>
                      <DayPickerInput
                        value={configuration.data.expenseSyncStartDate}
                        placeholder="Sync start date"
                        clearable={false}
                        onChange={(value) => handleValueChange('expenseSyncStartDate', value)}
                      />
                    </DatePickerContainer>
                  </Table.Cell>
                </Table.BoxRow>

                <Table.BoxRow>
                  <Table.Cell>
                    Default Xero Expense Account
                    <HelpTooltip
                      message="Ruddr will use this account when syncing expenses to Xero if an expense category is not mapped to a Xero account."
                      style={{ marginLeft: '.5rem' }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <OptionSelect
                      defaultOptions={expenseAccounts.data}
                      name="defaultExpenseAccountId"
                      typeLabel="Xero Expense Account"
                      value={configuration.data.defaultExpenseAccountId}
                      onChange={handleChange}
                    />
                  </Table.Cell>
                </Table.BoxRow>
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}

function OptionSelect({ defaultOptions, value, typeLabel, ...props }) {
  const [filterValue, setFilterValue] = useState('');

  const options = useMemo(() => {
    if (!filterValue) {
      return defaultOptions;
    }

    const fuse = new Fuse(defaultOptions, { keys: ['name'], threshold: 0.1 });
    const results = fuse.search(filterValue);
    return results.map((result) => result.item);
  }, [defaultOptions, filterValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const option = _.find(defaultOptions, { id: value });
    if (!option)
      return {
        id: value,
        name: (
          <Tooltip message={`${typeLabel} not found`}>
            <Icon icon="warning" color={colors.warning} spaceRight />
            {typeLabel} ID: {value}
          </Tooltip>
        ),
      };
    return option;
  }, [defaultOptions, value, typeLabel]);

  return (
    <SingleSelect
      showEmptyOption
      showFilter
      value={value}
      valueRenderer={selectedOption?.name ?? ''}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      {...props}>
      {options?.map((option) => (
        // eslint-disable-next-line react/no-unknown-property
        <option key={option.id} value={option.id} tooltip={option.name}>
          {option.name}
        </option>
      ))}
    </SingleSelect>
  );
}
